import { type LandingHeroCopy, MAX_LANDING_HERO_IMAGES } from '@repo/api-contracts';
import type { ChangeEvent, ReactElement } from 'react';
import {
  useInvalidateLandingHeroImages,
  useLandingHeroImagesQuery,
} from '@/entities/landing-image';
import { usePublishedProductOptionsQuery } from '@/entities/product';
import {
  LandingHeroImageCard,
  useAddLandingHeroImage,
  useDeleteLandingHeroImage,
  useMoveLandingHeroImage,
  useReplaceLandingHeroImage,
  useSetLandingHeroImageCopy,
  useSetLandingHeroImageProduct,
} from '@/features/landing-hero-images';
import { ContentCard, EmptyState } from '@/shared/ui';

const ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * HOME 히어로 배너 관리 — 추가·교체·순서·삭제·연결 상품·배너 문구. 위에서부터 히어로에 차례로 나온다.
 * 배너 문구는 직접 입력한 값이 먼저이고, 비운 칸은 배너마다 고른 연결 상품(상품명·가격·보기 링크)으로 채운다.
 */
export const LandingHeroManager = (): ReactElement => {
  const imagesQuery = useLandingHeroImagesQuery();
  const productOptionsQuery = usePublishedProductOptionsQuery();
  const invalidate = useInvalidateLandingHeroImages();
  const addAction = useAddLandingHeroImage({ onSuccess: invalidate });
  const replaceAction = useReplaceLandingHeroImage({ onSuccess: invalidate });
  const moveAction = useMoveLandingHeroImage({ onSuccess: invalidate });
  const deleteAction = useDeleteLandingHeroImage({ onSuccess: invalidate });
  const productAction = useSetLandingHeroImageProduct({ onSuccess: invalidate });
  const copyAction = useSetLandingHeroImageCopy({ onSuccess: invalidate });

  const images = imagesQuery.data ?? [];
  const productOptions = productOptionsQuery.data?.items ?? [];
  const isFull = images.length >= MAX_LANDING_HERO_IMAGES;
  // 요청 하나라도 진행 중이면 모든 카드의 버튼과 이미지 추가를 함께 잠근다 — 순서·목록이 같이 바뀌는
  // 동안 사용자가 다른 조작을 하면 꼬일 수 있어서다. 새 요청은 오류 줄을 비우며 reset 하는데, 진행 중인
  // 요청을 reset 하면 그 요청의 잠금과 실패 사유도 함께 사라진다.
  const isBusy =
    addAction.isPending ||
    moveAction.isPending ||
    deleteAction.isPending ||
    productAction.isPending ||
    copyAction.isPending ||
    replaceAction.pendingId !== null;
  const errorMessage =
    addAction.error ??
    replaceAction.error ??
    moveAction.error?.message ??
    deleteAction.error?.message ??
    productAction.error?.message ??
    copyAction.error?.message ??
    null;

  // 추가·교체·이동·삭제·상품 연결·문구 저장은 오류 한 줄(errorMessage)을 함께 쓴다. 새 요청을 보내기 전에 모든 요청의
  // 지난 오류를 모두 지운다 — 아니면 다른 요청이 성공해도 옛 오류가 그 요청이 다시 돌 때까지 남는다.
  const clearErrors = (): void => {
    addAction.reset();
    replaceAction.reset();
    moveAction.reset();
    deleteAction.reset();
    productAction.reset();
    copyAction.reset();
  };

  const handleAdd = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (file !== undefined) {
      clearErrors();
      void addAction.addImage(file);
    }
    event.target.value = '';
  };

  const handleReplace = (id: string, file: File): void => {
    clearErrors();
    void replaceAction.replaceImage(id, file);
  };

  const handleMove = (id: string, direction: 'UP' | 'DOWN'): void => {
    clearErrors();
    moveAction.moveImage(id, direction);
  };

  const handleChangeProduct = (id: string, productId: string | null): void => {
    clearErrors();
    productAction.setProduct(id, productId);
  };

  const handleSaveCopy = (id: string, copy: LandingHeroCopy): void => {
    clearErrors();
    copyAction.setCopy(id, copy);
  };

  const handleDelete = (id: string, order: number): void => {
    if (window.confirm(`히어로 이미지 ${order}번을 삭제하시겠습니까?`)) {
      clearErrors();
      deleteAction.deleteImage(id);
    }
  };

  return (
    <ContentCard
      title="HOME 히어로 배너"
      description={`HOME 첫 화면에 위에서부터 차례로 6초마다 나옵니다. 최대 ${MAX_LANDING_HERO_IMAGES}장.`}
      actions={
        <label className="inline-flex h-9 cursor-pointer items-center rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring">
          {addAction.isPending ? '올리는 중…' : '이미지 추가'}
          <input
            type="file"
            accept={ACCEPT}
            aria-label="이미지 추가"
            disabled={isFull || isBusy}
            onChange={handleAdd}
            className="sr-only"
          />
        </label>
      }
    >
      {/* 히어로는 사진을 키우지 않고 화면 높이에 맞춰 오른쪽에 붙인다 — 이 규격이어야 큰 화면에서도 선명하고,
          왼쪽 문구와 상품이 겹치지 않는다. */}
      <ul className="list-disc space-y-0.5 pl-5 text-sm text-muted-foreground">
        <li>
          권장 크기 <strong className="text-foreground">2560 × 1440px</strong> (가로:세로 16:9),
          JPG·PNG
        </li>
        <li>상품은 오른쪽 절반에 두고, 왼쪽은 문구가 올라가니 단색 배경으로 비워 주세요</li>
        <li>
          문구는 배너마다 직접 입력한 <strong className="text-foreground">배너 문구</strong>가 먼저
          나오고, 비운 칸은 연결 상품의 이름·가격·보기 링크로 채워집니다 — 둘 다 없으면 사진만
          나옵니다
        </li>
      </ul>

      {errorMessage !== null ? (
        <p role="alert" className="mt-3 text-sm text-destructive">
          {errorMessage}
        </p>
      ) : null}

      {imagesQuery.isPending ? (
        <div className="mt-6">
          <EmptyState>불러오는 중…</EmptyState>
        </div>
      ) : imagesQuery.isError ? (
        <div className="mt-6">
          <EmptyState isError>
            히어로 이미지를 불러오지 못했습니다.{' '}
            <button type="button" className="underline" onClick={() => void imagesQuery.refetch()}>
              다시 시도
            </button>
          </EmptyState>
        </div>
      ) : images.length === 0 ? (
        <div className="mt-6">
          <EmptyState>
            등록된 히어로 이미지가 없습니다. 이미지를 추가하면 HOME 첫 화면 배경에 나옵니다.
          </EmptyState>
        </div>
      ) : (
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {images.map((image, index) => (
            <LandingHeroImageCard
              key={image.id}
              image={image}
              productOptions={productOptions}
              order={index + 1}
              isFirst={index === 0}
              isLast={index === images.length - 1}
              isBusy={isBusy}
              onMove={(direction) => handleMove(image.id, direction)}
              onReplace={(file) => handleReplace(image.id, file)}
              onDelete={() => handleDelete(image.id, index + 1)}
              onChangeProduct={(productId) => handleChangeProduct(image.id, productId)}
              onSaveCopy={(copy) => handleSaveCopy(image.id, copy)}
            />
          ))}
        </ul>
      )}
    </ContentCard>
  );
};
